import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { Button, Dropdown } from 'antd';
import { toast } from 'react-toastify';
import { queryClient } from 'src/query-client';
import type { ItemType } from 'antd/lib/menu/hooks/useItems';
import { useCurrentWorkspace } from '@hooks/workspace';
import { useOrganization } from '@components/OrganizationBoundary';
import { ActionToast } from '@components/Toasts';
import { download } from '@helpers/download';
import { useAuth } from '@hooks/useAuth';
import { MixpanelEventType, MixpanelService } from '@services/mixpanelService';
import {
  restoreDeletedAssetAction,
  setFavoriteAssetAction
} from '@redux/actions/assetsAction';
import {
  toggleShareAssetModal,
  toggleRenameEntityModal,
  toggleEntityDeletionModal,
  toggleManageAssetVersionsModal,
  toggleApprovalModal,
  toggleApprovalAssetModal,
  toggleCopyModal,
  toggleMoveModal,
  toggleAddToExistingLinkModal
} from '@redux/actions/modalAction';
import { AssetItemDto, AVMetadataInfoDto } from '@api/Api';
import { useCopyAssetsMutation } from '@hooks/queries/assets';
import {
  ActionIcon,
  ApprovalRoutingIcon,
  DeleteIcon,
  DownloadInvoiceIcon,
  DropdownCopyIcon,
  DropdownDuplicateIcon,
  DropdownLinkIcon,
  DropdownLinkPlusIcon,
  DropdownRenameIcon,
  DropdownVersionsIcon,
  HeartIcon
} from '@icons';

type AssetActionsProps = {
  asset: AssetItemDto;
  canShare?: boolean;
  page: string;
};

function AssetActions({ asset, canShare, page }: AssetActionsProps) {
  const dispatch = useDispatch();
  const { user } = useAuth(true, 'internal');
  const navigate = useNavigate();
  const [isCardMenuVisible, setIsCardMenuVisible] = useState<boolean>(false);
  const { currentOrganization } = useOrganization();
  const [currentWorkspace] = useCurrentWorkspace(false);

  const canCreateAw = !!currentWorkspace?.permissions.createApprovalWorkflows;
  const awFeatureEnabled =
    !!asset.asset?.approvalWorkflow ||
    !!currentOrganization.entity?.features.approvalRouting;
  const canManageAW = !!asset.asset?.approvalWorkflow?.permissions.manage;

  const { mutateAsync: copyAssets, isPending: isCopyingAssets } =
    useCopyAssetsMutation({});

  const callbackAwModal = () => {
    queryClient.invalidateQueries({ queryKey: ['assets', 'list'] });
  };

  const awLabel = useMemo(() => {
    if (!!asset.asset?.approvalWorkflow && canManageAW) {
      return 'Manage approval routing';
    }
    if (!asset.asset?.approvalWorkflow && canCreateAw) {
      return 'Create approval routing';
    }
    return 'View approval routing';
  }, [asset.asset?.approvalWorkflow, canManageAW, canCreateAw]);

  const menuItems: ItemType[] = [
    ...(asset.asset?.permissions.download &&
    asset.asset?.versions[0].metadata.type === 'video'
      ? [
          {
            key: 'download',
            icon: <DownloadInvoiceIcon />,
            label: 'Download',
            children: [
              {
                key: 'original',
                label: (
                  <>
                    Original{' '}
                    {
                      asset.asset?.versions[0].metadata.info.avInfo?.video
                        ?.height
                    }
                    p
                  </>
                ),
                onClick: () => {
                  download(
                    asset.asset?.versions[0].sourceUrl,
                    asset.asset?.versions[0].name
                  );
                }
              },
              {
                type: 'divider' as const
              },
              ...Object.entries(
                asset.asset?.versions[0].metadata.videoProxies || {}
              )
                .reverse()
                .filter((el) => el[1].status !== 'skipped')
                .map((el) => {
                  const info = asset.asset?.versions[0].metadata
                    .info as AVMetadataInfoDto;
                  if (`${info.avInfo?.video?.height}` === el[0])
                    return undefined;

                  return {
                    key: el[0],
                    label: `${el[0]}p mp4`,
                    disabled: el[1].status !== 'ready',
                    onClick: () => {
                      download(el[1].url);
                    }
                  };
                })
                .filter(
                  (item): item is NonNullable<typeof item> => item != null
                )
            ]
          }
        ]
      : []),
    ...(asset.asset?.permissions.download &&
    asset.asset?.versions[0].metadata.type !== 'video'
      ? [
          {
            key: 'download',
            icon: <DownloadInvoiceIcon />,
            disabled: !asset.asset?.permissions.download,
            label: 'Download',
            onClick: () => {
              if (!asset.asset) return;
              const { name, sourceUrl } = asset.asset.versions[0];
              download(sourceUrl, name);
              setIsCardMenuVisible(false);
            }
          }
        ]
      : []),
    ...(canShare
      ? [
          {
            key: 'share',
            label: 'New share link',
            icon: <DropdownLinkPlusIcon />,
            onClick: () => {
              dispatch(
                toggleShareAssetModal({
                  visible: true,
                  campaignId: asset.campaignId,
                  assets: [
                    { id: asset.id, versionId: asset.asset?.versions[0]?.id }
                  ],
                  defaultName: asset.asset?.versions[0]?.name || ''
                })
              );
              setIsCardMenuVisible(false);
            }
          },
          {
            key: 'add-to-existing-link',
            label: 'Add to existing link',
            icon: <DropdownLinkIcon />,
            onClick: () => {
              dispatch(
                toggleAddToExistingLinkModal({
                  visible: true,
                  campaignId: asset.campaignId,
                  assets: [
                    { id: asset.id, versionId: asset.asset?.versions[0]?.id }
                  ]
                })
              );
              setIsCardMenuVisible(false);
            }
          }
        ]
      : []),
    {
      type: 'divider'
    },
    ...(asset.asset?.permissions.rename
      ? [
          {
            key: 'rename',
            icon: <DropdownRenameIcon />,
            label: 'Rename',
            onClick: () => {
              dispatch(
                toggleRenameEntityModal({
                  visible: true,
                  entity: asset,
                  page: 'media'
                })
              );
              setIsCardMenuVisible(false);
            }
          }
        ]
      : []),
    {
      key: 'duplicate-folder',
      label: 'Duplicate',
      disabled: isCopyingAssets,
      icon: <DropdownDuplicateIcon />,
      onClick: () => {
        copyAssets({
          workspaceId: asset.workspaceId,
          assetIds: [asset.id],
          targetCampaignId: asset.campaignId,
          targetFolderId: asset.path.slice(-1)[0]?.id,
          copyComments: true
        });
        setIsCardMenuVisible(false);
      }
    },
    {
      key: 'copy-to',
      label: 'Copy to',
      icon: <DropdownCopyIcon />,
      onClick: () => {
        dispatch(
          toggleCopyModal({
            visible: true,
            type: 'assets',
            assets: [
              {
                id: asset.id,
                name: `${asset.asset?.versions[0]?.name}.${asset.asset?.versions[0]?.extension}`
              }
            ]
          })
        );
        setIsCardMenuVisible(false);
      }
    },
    ...(asset.asset?.permissions.moveToPrivateFolder ||
    asset.asset?.permissions.moveToPublicFolder
      ? [
          {
            key: 'move_to',
            label: 'Move to',
            icon: <DropdownCopyIcon />,
            onClick: () => {
              dispatch(
                toggleMoveModal({
                  visible: true,
                  type: 'assets',
                  assets: [
                    {
                      id: asset.id,
                      name: `${asset.asset?.versions[0]?.name}.${asset.asset?.versions[0]?.extension}`
                    }
                  ],
                  campaignId: asset.campaignId,
                  campaignName: asset.campaignName,
                  permissions: {
                    moveToCampaign:
                      asset.asset?.permissions.moveToCampaign ?? false,
                    moveToPublicFolder:
                      asset.asset?.permissions.moveToPublicFolder ?? false,
                    moveToPrivateFolder:
                      asset.asset?.permissions.moveToPrivateFolder ?? false
                  }
                })
              );
              setIsCardMenuVisible(false);
            }
          }
        ]
      : []),
    {
      type: 'divider'
    },
    ...(awFeatureEnabled
      ? [
          {
            key: 'manage-aw',
            icon: <ApprovalRoutingIcon />,
            label: awLabel,
            onClick: () => {
              setIsCardMenuVisible(false);
              if (!asset.asset?.versions[0]) return;
              if (!currentWorkspace?.id) return;
              if (asset.asset?.approvalWorkflow) {
                dispatch(
                  toggleApprovalAssetModal({
                    assetId: asset.id,
                    visible: true,
                    versionId: asset.asset.versions[0].id,
                    approvalWorkflowId: asset.asset?.approvalWorkflow.id,
                    callback: callbackAwModal
                  })
                );
                return;
              }
              dispatch(
                toggleApprovalModal({
                  visible: true,
                  approvalWorkflowId: asset.asset?.approvalWorkflow?.id,
                  assetVersions: [
                    {
                      ...asset.asset?.versions[0],
                      assetId: asset.id
                    }
                  ],
                  permissions: {
                    create: canCreateAw
                  },
                  campaignId: asset.campaignId,
                  callback: (aw) => {
                    if (aw) {
                      navigate(
                        `/campaigns/approvals/${aw.workspaceId}/${aw.campaignId}/${aw.id}`
                      );
                      return;
                    }
                    callbackAwModal();
                  }
                })
              );
            }
          }
        ]
      : []),
    {
      key: 'manage-version-stack',
      label: 'Manage version stack',
      icon: <DropdownVersionsIcon />,
      onClick: () => {
        dispatch(
          toggleManageAssetVersionsModal({
            versions: asset?.asset?.versions || [],
            permissions: asset?.asset?.permissions || null,
            versionsCount: asset?.asset?.versionsCount || 0,
            visible: true,
            assetId: asset.id || ''
          })
        );
        setIsCardMenuVisible(false);
      }
    },
    {
      key: 'add_to_favorites',
      label: asset?.isFavorite ? 'Remove from favorites' : 'Add to favorites',
      icon: (
        <HeartIcon className={classNames({ 'b-favorite': asset.isFavorite })} />
      ),
      onClick: () => {
        if (asset.deletedAt) return;
        if (!asset?.isFavorite) {
          dispatch(
            setFavoriteAssetAction(
              {
                assetId: asset.id,
                isFavorite: !asset.isFavorite
              },
              page === 'favorite'
            )
          );
          MixpanelService.track(user.id, MixpanelEventType.MANAGE_ASSETS, {
            addToFavorite: true,
            deleteAsset: false,
            newAsset: false,
            externalLinkShare: false,
            approvalRouting: false,
            newCommentAsset: false,
            annotationLeft: false,
            taskFromComment: false
          });
          setIsCardMenuVisible(false);
          return;
        }
        dispatch(
          setFavoriteAssetAction(
            {
              assetId: asset.id,
              isFavorite: !asset.isFavorite
            },
            page === 'favorite'
          )
        );
        toast(
          <ActionToast
            title="Media has been removed from favorites"
            onUndo={undefined}
            description={undefined}
            closeToast={undefined}
          />,
          {
            hideProgressBar: true,
            bodyClassName: 'toast_container--invitation'
          }
        );

        setIsCardMenuVisible(false);
      }
    },
    ...(asset.asset?.permissions.delete
      ? [
          {
            type: 'divider' as const
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteIcon />,
            onClick: () => {
              dispatch(
                toggleEntityDeletionModal({
                  entity: asset,
                  page: 'asset',
                  permanent: false,
                  visible: true
                })
              );

              setIsCardMenuVisible(false);
            }
          }
        ]
      : [])
  ];

  const deleteMenuItems: ItemType[] = asset.asset?.permissions.delete
    ? [
        {
          key: 'delete_permanent',
          label: 'Delete forever',
          onClick: () => {
            dispatch(
              toggleEntityDeletionModal({
                entity: asset,
                page: 'asset',
                permanent: true,
                visible: true
              })
            );
            setIsCardMenuVisible(false);
          }
        },
        {
          key: 'restore',
          label: 'Restore',
          onClick: () => {
            dispatch(
              restoreDeletedAssetAction({
                assetId: asset.id
              })
            );
            setIsCardMenuVisible(false);
          }
        }
      ]
    : [];

  return (
    <Dropdown
      open={isCardMenuVisible}
      onOpenChange={(v) => setIsCardMenuVisible(v)}
      trigger={['click']}
      overlayClassName="asset_links_overlay"
      menu={{
        items: !asset.deletedAt ? menuItems : deleteMenuItems,
        onClick: ({ domEvent }) => domEvent.stopPropagation()
      }}
    >
      <Button
        type="text"
        size="small"
        className="asset_links_action"
        onClick={(e) => e.stopPropagation()}
      >
        <ActionIcon />
      </Button>
    </Dropdown>
  );
}

export default AssetActions;
